

import { forwardRef, useContext } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useDisabledAnchor } from '../../hooks/use-disabled-anchor';
import { useDisabledState } from '../../hooks/use-disabled-state';
import { ButtonGroupContext } from '../button-group/context/button-group.context';
import { Slot, Slottable } from '../slot';
import { ButtonLoading } from './button-loading';
import { ButtonRootContext } from './context/button-root.context';
import { buttonRecipe } from './styles/button-root.styles';
import type { ButtonRootPropsInterface } from './types/button-root.types';

/**
 * @since 10.0.0
 */
export const ButtonRoot = forwardRef<HTMLButtonElement, ButtonRootPropsInterface>(
  (props: ButtonRootPropsInterface, forwardedRef) => {
    const buttonGroupContext = useContext(ButtonGroupContext);
    const {
      asChild = false,
      tone = 'brand',
      emphasis = 'outlined',
      children,
      className,
      css: cssProp,
      disabled = false,
      fluid = buttonGroupContext.fluid || false,
      onImpact = false,
      size = buttonGroupContext.size || 'md',
      loading = false,
      type = 'button',
      ...rest
    } = props;

    const isDisabled = disabled || loading;
    const responsiveFluid = splitResponsiveVariant('fluid', fluid);

    const styles = css(
      buttonRecipe.raw({
        tone: buttonGroupContext.tone || tone,
        emphasis: buttonGroupContext.emphasis || emphasis,
        disabled: isDisabled,
        onImpact,
        loading,
        size: buttonGroupContext.size || size,
        ...responsiveFluid,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'button';

    const a11yDisabledState = useDisabledState({ disabled: isDisabled, focusable: true });

    const content = useDisabledAnchor({
      disabled: isDisabled,
      asChild,
      children,
      a11yProps: a11yDisabledState,
    });

    return (
      <ButtonRootContext.Provider
        value={{
          tone: buttonGroupContext.tone || tone,
          emphasis: buttonGroupContext.emphasis || emphasis,
          disabled: isDisabled,
          onImpact,
          size: buttonGroupContext.size || size,
          loading,
        }}
      >
        <Component
          ref={forwardedRef}
          data-c11n-component="Button.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'group', styles, className)}
          type={asChild ? undefined : type}
          {...rest}
          {...a11yDisabledState}
        >
          <Slottable>{content}</Slottable>
          {loading ? <ButtonLoading /> : null}
        </Component>
      </ButtonRootContext.Provider>
    );
  },
);

ButtonRoot.displayName = 'Button.Root';
